import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select, { components as RSComponents } from 'react-select';
import { ChevronIcon, CloseIcon, SearchIcon } from '../../../../bombiix-icon';
import { NewCheckbox } from '../NewCheckbox';

export interface NewSelectMultiSelectOption {
  label: string;
  value: string | number;
}

interface CSSStyleObject {
  [key: string]: string | number | boolean | undefined | CSSStyleObject;
}

interface StyleState {
  isFocused?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
}

type StyleFn = (_base: CSSStyleObject, _state?: StyleState) => CSSStyleObject;
type NewSelectStyles = Record<string, StyleFn>;

const FONT_BODY = "'Aktiv Grotesk', sans-serif";
const FONT_MEDIUM = "'Aktiv Grotesk', sans-serif";
const COLOR_TEXT = '#3c3c3c';
const COLOR_PLACEHOLDER = '#a4a4a4';
const COLOR_BORDER = '#d8d8d8';
const COLOR_BORDER_HOVER = '#c2e5fb';
const COLOR_BORDER_ACTIVE = '#6dc1f5';
const COLOR_BORDER_INVALID = '#fa979b';
const COLOR_OPTION_HOVER = '#ceeafc';
const COLOR_OPTION_SELECTED = '#eaf6fe';
const COLOR_CHIP_BG = '#73c5af';

/**
 * Background for one option row. Keyboard focus outranks the chosen value: focus
 * is transient and has to stay visible while it exists — under `isMulti` the
 * focused row is often an already-picked one, and letting the selected tint win
 * there hid where Enter would land. Selection stays marked the rest of the time
 * (and multi-select also carries its ticked checkbox), so nothing is lost.
 * A disabled row stays flat whatever else is true of it: callers lock rows that
 * are still selected (an in-use colourway, supplier or factory), and tinting
 * those would read as actionable when they are deliberately greyed out.
 * @param {StyleState} state - react-select's per-option state.
 * @param {boolean} showFocusedOption - Whether the focus highlight may paint.
 * @returns {string} The background colour for the row.
 */
const optionBackground = (state: StyleState | undefined, showFocusedOption: boolean): string => {
  if (state?.isDisabled) return 'transparent';
  if (showFocusedOption && state?.isFocused) return COLOR_OPTION_HOVER;
  if (state?.isSelected) return COLOR_OPTION_SELECTED;
  return 'transparent';
};

/**
 * Builds the emotion style map react-select renders with.
 * @param {boolean} invalid - Paints the error border and alert affordance.
 * @param {boolean} showFocusedOption - Whether react-select's focused option may
 *   paint its highlight. False until the user navigates the menu by keyboard:
 *   react-select focuses option 0 the moment the menu opens, and painting that
 *   makes an untouched row read as already picked.
 * @param {number} maxMenuHeight - Hard cap for the option list. react-select 4.3.1
 *   hands the list all the space left below the control when the menu does not
 *   fit, without clamping it to `maxMenuHeight`, so the cap is re-applied here.
 * @returns {NewSelectStyles} The style map.
 */
const buildStyles = (invalid: boolean, showFocusedOption: boolean, maxMenuHeight: number): NewSelectStyles => ({
  control: (base: CSSStyleObject, state?: StyleState) => ({
    ...base,
    minHeight: '28px',
    height: '28px',
    border: `1px solid ${invalid ? COLOR_BORDER_INVALID : state?.isFocused ? COLOR_BORDER_ACTIVE : COLOR_BORDER}`,
    borderRadius: '4px',
    padding: '0',
    gap: '4px',
    boxShadow: 'none',
    fontFamily: FONT_BODY,
    fontSize: '14px',
    transition: 'border-color 0.15s ease',
    overflow: 'hidden',
    '&:hover': {
      borderColor: invalid ? COLOR_BORDER_INVALID : state?.isFocused ? COLOR_BORDER_ACTIVE : COLOR_BORDER_HOVER,
    },
  }),
  valueContainer: (base: CSSStyleObject) => ({
    ...base,
    padding: '0 0 0 10px',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    overflowX: 'hidden',
    overflowY: 'hidden',
    height: '100%',
  }),
  indicatorsContainer: (base: CSSStyleObject) => ({
    ...base,
    paddingRight: '12px',
  }),
  placeholder: (base: CSSStyleObject) => ({
    ...base,
    color: COLOR_PLACEHOLDER,
    fontFamily: FONT_BODY,
    fontSize: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }),
  singleValue: (base: CSSStyleObject) => ({
    ...base,
    color: COLOR_TEXT,
    fontFamily: FONT_BODY,
    fontSize: '14px',
  }),
  input: (base: CSSStyleObject) => ({
    ...base,
    color: COLOR_TEXT,
    fontFamily: FONT_BODY,
    fontSize: '14px',
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: CSSStyleObject) => ({
    ...base,
    padding: '0',
    color: COLOR_PLACEHOLDER,
    fontSize: '16px',
    '&:hover': { color: COLOR_TEXT },
  }),
  clearIndicator: (base: CSSStyleObject) => ({
    ...base,
    padding: '0 4px 0 0',
    color: COLOR_PLACEHOLDER,
    fontSize: '16px',
    cursor: 'pointer',
    '&:hover': { color: COLOR_TEXT },
  }),
  multiValue: (base: CSSStyleObject) => ({
    ...base,
    backgroundColor: COLOR_CHIP_BG,
    borderRadius: '11px',
    margin: '2px 4px 2px 0',
    padding: '2px 2px 2px 6px',
    gap: '4px',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 0.25px 0.25px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    flexShrink: 0,
    minWidth: 0,
    maxWidth: '160px',
  }),
  multiValueLabel: (base: CSSStyleObject) => ({
    ...base,
    color: COLOR_TEXT,
    fontFamily: FONT_BODY,
    fontSize: '12px',
    fontWeight: 400,
    padding: 0,
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '112px',
    '&:last-child': { paddingRight: '4px' },
  }),
  multiValueRemove: (base: CSSStyleObject) => ({
    ...base,
    color: COLOR_TEXT,
    padding: 0,
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
      color: COLOR_TEXT,
    },
  }),
  menu: (base: CSSStyleObject) => ({
    ...base,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.25)',
    marginTop: '4px',
    overflow: 'hidden',
    border: 'none',
  }),
  menuPortal: (base: CSSStyleObject) => ({ ...base, zIndex: 9999 }),
  noOptionsMessage: (base: CSSStyleObject) => ({
    ...base,
    padding: '2px 6px',
    color: COLOR_PLACEHOLDER,
    fontSize: '14px',
    lineHeight: '16px',
    textAlign: 'center',
  }),
  menuList: (base: CSSStyleObject) => ({
    ...base,
    maxHeight: typeof base.maxHeight === 'number' ? Math.min(base.maxHeight, maxMenuHeight) : maxMenuHeight,
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  }),
  option: (base: CSSStyleObject, state?: StyleState) => ({
    ...base,
    minHeight: '24px',
    height: 'auto',
    flexShrink: 0,
    padding: '4px 6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '4px',
    lineHeight: '16px',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    backgroundColor: optionBackground(state, showFocusedOption),
    color: state?.isDisabled ? COLOR_PLACEHOLDER : COLOR_TEXT,
    cursor: state?.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state?.isDisabled ? 0.5 : 1,
    fontFamily: FONT_MEDIUM,
    fontSize: '12px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: state?.isDisabled ? 'transparent' : COLOR_OPTION_HOVER,
    },
    '&:active': {
      backgroundColor: COLOR_OPTION_HOVER,
    },
  }),
});

let idCounter = 0;
const nextId = (): string => {
  idCounter += 1;
  return `new-select-${idCounter}`;
};

interface RSInnerProps {
  children?: React.ReactNode;
  [key: string]: unknown;
}

interface RSOptionInnerProps extends RSInnerProps {
  isSelected?: boolean;
  isDisabled?: boolean;
  data?: unknown;
  innerProps?: Record<string, unknown>;
  label?: string;
  hasValue?: boolean;
  selectProps?: { inputValue?: string };
}

interface RSMultiValueRemoveInnerProps<Option> extends RSInnerProps {
  data?: Option;
}

interface RSMenuListInnerProps extends RSInnerProps {
  clearValue?: () => void;
  getValue?: () => ReadonlyArray<unknown>;
  setValue?: (_value: ReadonlyArray<unknown>, _action?: string) => void;
  options?: ReadonlyArray<unknown>;
  selectProps?: {
    inputValue?: string;
    onInputChange?: (_value: string, _action: { action: string }) => void;
    getOptionLabel?: (_option: unknown) => string;
    getOptionValue?: (_option: unknown) => string;
    filterOption?: ((_option: { label: string; value: string; data: unknown }, _inputValue: string) => boolean) | null;
  };
}

const RSDropdownIndicator = RSComponents.DropdownIndicator as React.ComponentType<RSInnerProps>;
const RSClearIndicator = RSComponents.ClearIndicator as React.ComponentType<RSInnerProps>;
const RSMultiValueRemove = RSComponents.MultiValueRemove as React.ComponentType<RSInnerProps>;
const RSOption = RSComponents.Option as React.ComponentType<RSOptionInnerProps>;
const RSMenuList = RSComponents.MenuList as React.ComponentType<RSMenuListInnerProps>;

/**
 * Replace the default react-select SVG chevron with the Bombiix icon-font
 * glyph. Rotates 180° while the menu is open (`selectProps.menuIsOpen`) so
 * the chevron flips from ⌄ to ⌃, matching the master-design select spec.
 */
function BombiixDropdownIndicator(props: RSInnerProps) {
  const isOpen = Boolean((props.selectProps as { menuIsOpen?: boolean } | undefined)?.menuIsOpen);
  return (
    <RSDropdownIndicator {...props}>
      <ChevronIcon size={24} aria-hidden="true" style={{ transform: isOpen ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s ease' }} />
    </RSDropdownIndicator>
  );
}

/**
 * Replace the default clear-value SVG with the Bombiix close glyph.
 */
function BombiixClearIndicator(props: RSInnerProps) {
  return (
    <RSClearIndicator {...props}>
      <CloseIcon size={24} aria-hidden="true" />
    </RSClearIndicator>
  );
}

/**
 * Replace the per-chip remove SVG (multi-select) with the Bombiix close glyph.
 */
function BombiixMultiValueRemove(props: RSInnerProps) {
  return (
    <RSMultiValueRemove {...props}>
      <CloseIcon size={24} aria-hidden="true" />
    </RSMultiValueRemove>
  );
}

/**
 * Multi-select option row: react-select's option wrapper with a leading
 * checkbox that mirrors the selected state. Selection is driven by clicking
 * the row (react-select's own handler); the checkbox is presentational.
 */
function CheckboxOption(props: RSOptionInnerProps) {
  return (
    <RSOption {...props}>
      <span className="bombiix-select__opt">
        <NewCheckbox className="bombiix-select__opt-check" size="sm" checked={Boolean(props.isSelected)} readOnly tabIndex={-1} />
        <span className="bombiix-select__opt-label">{props.children}</span>
      </span>
    </RSOption>
  );
}

/**
 * Wraps the option list with optional search input, Select All row and Clear
 * All footer. Only built when at least one of the toggles is passed.
 *   - `showSearch`  → renders an in-menu search field that drives
 *     `selectProps.inputValue`, so react-select's own filter picks it up.
 *   - `selectAllLabel` → renders a top-of-list Select All row (multi-only);
 *     toggles between "select every option" and "deselect all".
 *   - `clearAllLabel` → renders a footer button that clears the current value.
 */
function buildMenuList({
  showSearch,
  searchPlaceholder,
  selectAllLabel,
  clearAllLabel,
  menuRef,
  suppressCloseRef,
}: {
  showSearch: boolean | undefined;
  searchPlaceholder: string | undefined;
  selectAllLabel: string | undefined;
  clearAllLabel: string | undefined;
  menuRef: React.RefObject<HTMLDivElement | null>;
  suppressCloseRef: React.RefObject<boolean>;
}) {
  return function BombiixMenuList(props: RSMenuListInnerProps) {
    const inputValue = props.selectProps?.inputValue ?? '';
    const handleSearch = (next: string) => {
      props.selectProps?.onInputChange?.(next, { action: 'input-change' });
    };
    const selectedValues = props.getValue?.() ?? [];
    const totalOptions = props.options?.length ?? 0;
    const getOptionKey = (option: unknown): unknown => props.selectProps?.getOptionValue?.(option) ?? option;
    const selectedKeys = new Set(selectedValues.map(getOptionKey));
    const matchingOptions = getSearchMatchedOptions(props.options ?? [], inputValue, props.selectProps);
    const allSelected = inputValue
      ? matchingOptions.length > 0 && matchingOptions.every((option) => selectedKeys.has(getOptionKey(option)))
      : totalOptions > 0 && selectedValues.length === totalOptions;
    const handleSelectAll = () => {
      if (!inputValue) {
        props.setValue?.(allSelected ? [] : (props.options ?? []), allSelected ? 'deselect-option' : 'select-option');
        return;
      }
      if (allSelected) {
        const matchingKeys = new Set(matchingOptions.map(getOptionKey));
        props.setValue?.(
          selectedValues.filter((option) => !matchingKeys.has(getOptionKey(option))),
          'deselect-option',
        );
      } else {
        props.setValue?.([...selectedValues, ...matchingOptions.filter((option) => !selectedKeys.has(getOptionKey(option)))], 'select-option');
      }
    };
    return (
      <div className="bombiix-select__menu-extras" data-bombiix-select-menu="" ref={menuRef}>
        {showSearch ? (
          <>
            <div className="bombiix-select__search">
              <input
                type="text"
                className="bombiix-select__search-input"
                value={inputValue}
                placeholder={searchPlaceholder ?? 'Search'}
                onChange={(e) => handleSearch(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
              <SearchIcon size={24} aria-hidden="true" />
            </div>
            <div className="bombiix-select__separator" aria-hidden="true" />
          </>
        ) : null}
        {selectAllLabel ? (
          <button
            type="button"
            className="bombiix-select__select-all"
            onMouseDown={(e) => {
              e.preventDefault();
              suppressCloseRef.current = true;
            }}
            onClick={handleSelectAll}
          >
            <NewCheckbox className="bombiix-select__select-all-check" size="sm" checked={allSelected} readOnly tabIndex={-1} />
            <span className="bombiix-select__select-all-label">{selectAllLabel}</span>
          </button>
        ) : null}
        <RSMenuList {...props}>{props.children}</RSMenuList>
        {clearAllLabel ? (
          <button
            type="button"
            className="bombiix-select__clear-all"
            onMouseDown={(e) => {
              e.preventDefault();
              suppressCloseRef.current = true;
            }}
            onClick={() => props.clearValue?.()}
          >
            {clearAllLabel}
          </button>
        ) : null}
      </div>
    );
  };
}

export interface NewSelectMultiSelectProps<Option = NewSelectMultiSelectOption> {
  options?: ReadonlyArray<Option>;
  value?: unknown;
  defaultValue?: unknown;
  onChange?(_value: Option | ReadonlyArray<Option> | null, _action?: unknown): void;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  name?: string;
  getOptionLabel?(_option: Option): string;
  getOptionValue?(_option: Option): string;
  /** Custom renderer for an option's label — forwarded to react-select, for rows that need more than plain text (e.g. icon + label). */
  isOptionDisabled?: (_option: Option) => boolean;
  /**
   * Custom render for a single option, replacing the plain `getOptionLabel` string. Called for
   * the menu row and again for the selected value in the control — `_meta.context` says which,
   * so one function can render both differently. Under `isMulti` the wrapper's own checkbox
   * Option wraps the returned node, so it lands inside the row's label rather than replacing it.
   */
  formatOptionLabel?(_option: Option, _meta: { context: 'menu' | 'value'; inputValue: string; selectValue: ReadonlyArray<Option> }): React.ReactNode;
  noOptionsMessage?: () => React.ReactNode;
  inputId?: string;
  menuPlacement?: 'auto' | 'top' | 'bottom';
  menuPosition?: 'absolute' | 'fixed';
  /** Portal target for the menu — pass document.body to escape overflow/transform clipping (e.g. AG Grid cells). */
  menuPortalTarget?: HTMLElement | null;
  /** Fired when the menu closes — used by grid cells to commit a multi-select pick once, on close. */
  onMenuClose?: () => void;
  /** Fired when the menu opens. Chained with this wrapper's own open handling, never replaced. */
  onMenuOpen?: () => void;
  onKeyDown?: (_event: React.KeyboardEvent<HTMLDivElement>) => void;
  /** Height cap, in px, of the option list; it scrolls beyond this. Defaults to 200. */
  maxMenuHeight?: number;
  styles?: NewSelectStyles;
  label?: string;
  /** When true (and `label` is set), renders a red `*` after the label to mark the field as mandatory. */
  required?: boolean;
  /** When true (and `label` is set), renders a green `*` marking the field as DPP-required. */
  dppRequired?: boolean;
  containerClassName?: string;
  invalid?: boolean;
  /** Applies the blue-outline highlight skin used by the bulk-update visual. */
  isHighlight?: boolean;
  /** Shows a "Clear All" / "Remove All" footer button under the option list when passed. Omit to hide it. */
  clearAllLabel?: string;
  /** Renders an in-menu search field at the top of the option list. Filter runs via react-select's own `inputValue`. */
  showSearch?: boolean;
  /** Placeholder shown in the in-menu search field (defaults to "Search"). */
  searchPlaceholder?: string;
  /** Renders a "Select All" checkbox row above the option list. Multi-select only; toggles all vs none. */
  selectAllLabel?: string;
  /** react-select passthrough. Set false to keep the picks out of the control so they can be rendered as chips below it. */
  controlShouldRenderValue?: boolean;
  /** Renders the label in the medium font face instead of regular. */
  isFontMedium?: boolean;
  /**
   * Multi-select with `isOptionDisabled` only: fired with the locked options whenever the user tries
   * to remove one — clicking its row, Clear All, the Select All toggle-off, the clear indicator or
   * Backspace. The locked values are kept either way; this lets the consumer say why.
   */
  onLockedRemoval?: (_lockedOptions: Option[]) => void;
  /** Label of the option to scroll into view when the menu opens with nothing selected (e.g. the current year). */
  scrollToLabel?: string;
}

/**
 * NewSelectMultiSelect — react-select wrapper for PIM (single + multi).
 * Multi-select (isMulti) renders a checkbox in each option, keeps the menu open
 * across picks, and shows the picks as removable chips in the control.
 * Indicators use the Bombiix icon-font glyphs. onChange returns the original
 * option objects. Pass invalid=true for error border + alert icon.
 */
function NewSelectMultiSelect<Option = NewSelectMultiSelectOption>({
  label,
  required,
  dppRequired,
  containerClassName,
  invalid,
  isHighlight,
  styles,
  isMulti,
  placeholder,
  clearAllLabel,
  showSearch,
  searchPlaceholder,
  selectAllLabel,
  isFontMedium = false,
  maxMenuHeight = DEFAULT_MENU_MAX_HEIGHT,
  onLockedRemoval,
  scrollToLabel,
  ...rest
}: NewSelectMultiSelectProps<Option>) {
  /*
   * react-select focuses option 0 as soon as the menu opens, which painted a
   * highlight on a row the user had not hovered or keyed to — it read as already
   * selected. The highlight is held back until a key press says the user is
   * driving the menu from the keyboard, and a mouse press on the control hands
   * it back; mouse users get the same colour from `:hover`. Resetting on the
   * mouse rather than on menu-open is what lets a menu opened BY the keyboard
   * paint: react-select runs our `onKeyDown` before it opens the menu, so an
   * open-time reset wiped the flag the opening key press had just set.
   */
  const [isKeyNavigating, setIsKeyNavigating] = useState(false);
  const mergedStyles: NewSelectStyles = { ...buildStyles(Boolean(invalid), isKeyNavigating, maxMenuHeight), ...(styles ?? {}) };
  const rootClass = ['bombiix-select', isHighlight ? 'bombiix-select--bulk' : '', containerClassName].filter(Boolean).join(' ');
  const inputId = rest.inputId ?? (label ? nextId() : undefined);
  const labelClass = ['bombiix-select__label', invalid ? 'bombiix-select__label--invalid' : '', isFontMedium ? 'bombiix-select__label--medium' : '']
    .filter(Boolean)
    .join(' ');
  const hasMenuExtras = Boolean(showSearch || selectAllLabel || clearAllLabel);
  /* When the menu grows extras (search / select-all / clear-all) we own the
  open state — otherwise clicking those elements pulls focus away from
  react-select's internal input and its onBlur closes the menu. The deferred
  close lets us check whether focus has moved *inside* our own container OR
  the extras portion of the menu (which can be portalled outside) and skip
  closing in that case. */
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuExtrasRef = useRef<HTMLDivElement | null>(null);
  /**
   * Select All / Clear All call `preventDefault()` on mousedown so focus never
   * leaves react-select's own input — that's how they avoid closing the menu
   * while a pick is in progress, but it also means "focus is still on the
   * control" can't distinguish that from a legitimate close (e.g. clicking the
   * chevron again). Those two buttons flag this ref instead; handleMenuClose
   * reads and clears it rather than trusting focus location for that case.
   */
  const suppressCloseRef = useRef(false);
  const chevronCloseRef = useRef(false);
  const CustomMenuList = useMemo(
    () =>
      hasMenuExtras ? buildMenuList({ showSearch, searchPlaceholder, selectAllLabel, clearAllLabel, menuRef: menuExtrasRef, suppressCloseRef }) : undefined,
    [hasMenuExtras, showSearch, searchPlaceholder, selectAllLabel, clearAllLabel],
  );
  const [menuOpen, setMenuOpen] = useState(false);
  /**
   * Opening the menu focuses react-select's internal search input, which sits
   * after every chip in the nowrap/overflow-hidden value container — the
   * browser's native focus-scroll snaps that row all the way right, shifting
   * already-picked chips out of view even though nothing was selected. Restore
   * the pre-open scroll position one frame later, after the browser's own
   * scroll has already happened.
   */
  const fixValueContainerScroll = useCallback(() => {
    const valueContainer = containerRef.current?.querySelector<HTMLElement>('.bombiix-select__value-container');
    if (!valueContainer) return;
    const { scrollLeft } = valueContainer;
    requestAnimationFrame(() => {
      valueContainer.scrollLeft = scrollLeft;
    });
  }, []);
  const handleMenuOpen = useCallback(() => {
    fixValueContainerScroll();
    rest.onMenuOpen?.();
    setMenuOpen(true);
  }, [fixValueContainerScroll, rest]);

  const handleUncontrolledMenuOpen = useCallback(() => {
    fixValueContainerScroll();
    rest.onMenuOpen?.();
  }, [fixValueContainerScroll, rest]);

  /**
   * Lets the focused option paint once the user drives the menu from the
   * keyboard. Any key counts, not just the movement keys: typing to filter
   * re-focuses option 0 of the narrowed list, and Enter picks whatever is
   * focused — so a search that paints nothing would commit an unhighlighted row.
   * @param {React.KeyboardEvent<HTMLDivElement>} event - The key press.
   * @returns {void}
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      setIsKeyNavigating(true);
      rest.onKeyDown?.(event);
    },
    [rest],
  );
  const handleMenuClose = useCallback(() => {
    window.setTimeout(() => {
      if (chevronCloseRef.current) {
        chevronCloseRef.current = false;
        suppressCloseRef.current = false;
        setMenuOpen(false);
        rest.onMenuClose?.();
        return;
      }
      if (suppressCloseRef.current) {
        suppressCloseRef.current = false;
        return;
      }
      const focusedEl = document.activeElement;
      /* Keep the menu open while focus stays inside our control OR the extras (search / Select All /
         Clear All). The extras can be PORTALLED to <body> (grid cells pass menuPortalTarget), so
         they sit outside containerRef — check the portal too. Without the portal check, clicking the
         in-menu search or Select All row blurs react-select's input, activeElement lands on that
         portalled node (outside containerRef), and the menu wrongly closes on first click. */
      const inControl = containerRef.current?.contains(focusedEl);
      const inExtras = menuExtrasRef.current?.contains(focusedEl);
      const inPortalMenu = focusedEl instanceof Element && focusedEl.closest('.bombiix-select__menu-portal, .bombiix-select__menu') !== null;
      if (inControl || inExtras || inPortalMenu) {
        return;
      }
      setMenuOpen(false);
      rest.onMenuClose?.();
    }, 0);
  }, [rest]);

  /**
   * Close on any pointer press outside the control and outside the menu.
   * `handleMenuClose` alone is not enough for the controlled (menu-extras)
   * path: it only runs when react-select blurs its own input, and the extras'
   * `preventDefault()` on mousedown is designed to keep that focus put — so a
   * click on a non-focusable area of the page (a grid cell, a card, the page
   * background) left the menu hanging open. Listening on `pointerdown` in the
   * capture phase closes before any consumer's own click handler runs.
   */
  useEffect(() => {
    if (!hasMenuExtras || !menuOpen) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Node ? event.target : null;
      if (!target) return;
      const inControl = containerRef.current?.contains(target);
      const inExtras = menuExtrasRef.current?.contains(target);
      const inPortalMenu = target instanceof Element && target.closest('.bombiix-select__menu-portal, .bombiix-select__menu') !== null;
      if (inControl || inExtras || inPortalMenu) return;
      suppressCloseRef.current = false;
      setMenuOpen(false);
      rest.onMenuClose?.();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [hasMenuExtras, menuOpen, rest]);

  const isOptionDisabledProp = rest.isOptionDisabled;
  /**
   * Stable comparison key for an option: prefers the consumer's own `getOptionValue`
   * (matches how react-select itself identifies options) and falls back to `.value`.
   */
  const optionKey = useCallback(
    (option: Option): unknown => {
      if (rest.getOptionValue) return rest.getOptionValue(option);
      return option !== null && typeof option === 'object' && 'value' in option ? (option as { value: unknown }).value : option;
    },
    [rest.getOptionValue],
  );

  /**
   * The single `onChange` seam, carrying two independent guarantees.
   *
   * Locked-value guard (multi-select): `isOptionDisabled` alone only blocks the option
   * row inside the open MENU — react-select still lets a chip's remove control, "Clear All",
   * the "Select All" toggle-off, the ClearIndicator and Backspace drop an already-selected,
   * disabled value, because each of those paths calls `onChange` directly rather than going
   * through the menu. Intercepting here catches all of them in one place: any previously
   * selected value that satisfies `isOptionDisabled` and is missing from the next value is
   * merged back in before the consumer's own `onChange` runs.
   *
   * Close on select (single-select): react-select closes on pick by default, which the
   * controlled path loses by owning `menuIsOpen`. `handleMenuClose` cannot do it — after a
   * pick, focus is still on react-select's input inside `containerRef`, indistinguishable
   * from the search / Select All case the `inControl` guard exists to protect. Under
   * `isMulti` the menu deliberately stays open across picks, so the two guarantees never
   * apply to the same call.
   * @param {unknown} newValue - The new selection react-select emits.
   * @param {unknown} actionMeta - The react-select action metadata.
   * @returns {void}
   */
  const handleChange = useCallback(
    (newValue: unknown, actionMeta: unknown): void => {
      if (isMulti && isOptionDisabledProp) {
        const prevValue = Array.isArray(rest.value) ? (rest.value as Option[]) : [];
        const nextValue = Array.isArray(newValue) ? (newValue as Option[]) : [];
        const stillLocked = prevValue.filter((option) => isOptionDisabledProp(option) && !nextValue.some((next) => optionKey(next) === optionKey(option)));
        const mergedValue = stillLocked.length > 0 ? [...stillLocked, ...nextValue] : nextValue;
        rest.onChange?.(mergedValue, actionMeta);
        if (stillLocked.length > 0) {
          onLockedRemoval?.(stillLocked);
        }
        return;
      }
      rest.onChange?.(newValue as Option | ReadonlyArray<Option> | null, actionMeta);
      if (!isMulti) {
        setMenuOpen(false);
      }
    },
    [isMulti, isOptionDisabledProp, rest.value, rest.onChange, optionKey, onLockedRemoval],
  );

  const controlledMenuProps = hasMenuExtras
    ? { menuIsOpen: menuOpen, onMenuOpen: handleMenuOpen, onMenuClose: handleMenuClose, onChange: handleChange }
    : { onMenuOpen: handleUncontrolledMenuOpen };

  /**
   * Hides the remove control on a locked chip (a selected value for which `isOptionDisabled`
   * is true) so the UI doesn't offer a control that `handleChange` above would silently no-op.
   * Falls back to the plain `BombiixMultiValueRemove` when no predicate is supplied.
   */
  const MultiValueRemoveComponent = useMemo(() => {
    if (!isOptionDisabledProp) return BombiixMultiValueRemove;
    return function LockAwareMultiValueRemove(props: RSMultiValueRemoveInnerProps<Option>) {
      if (props.data !== undefined && isOptionDisabledProp(props.data)) return null;
      return <BombiixMultiValueRemove {...props} />;
    };
  }, [isOptionDisabledProp]);

  /**
   * A multi-select lock applies only to a value that is ALREADY selected — it prevents removal,
   * never addition. An option the predicate matches but which is not currently selected stays
   * pickable, so a value that was removed earlier can always be added back. Single-select passes
   * the consumer's predicate straight through, since there is nothing to "keep".
   */
  const lockedIfSelected = useCallback(
    (option: Option): boolean => {
      if (!isOptionDisabledProp) return false;
      if (!isMulti) return isOptionDisabledProp(option);
      const currentValue = Array.isArray(rest.value) ? (rest.value as Option[]) : [];
      return isOptionDisabledProp(option) && currentValue.some((selected) => optionKey(selected) === optionKey(option));
    },
    [isOptionDisabledProp, isMulti, rest.value, optionKey],
  );

  /**
   * react-select detaches the click handler from a disabled row, so clicking a locked option is a
   * silent no-op. When the consumer passed `onLockedRemoval`, a locked row gets a click handler that
   * reports it instead. Only a selected locked value renders disabled here (see `lockedIfSelected`).
   * The latest callback is read through a ref so an inline consumer arrow does not hand react-select
   * a new Option component — and remount every row — on each render.
   */
  const onLockedRemovalRef = useRef(onLockedRemoval);
  onLockedRemovalRef.current = onLockedRemoval;
  const hasLockedRemoval = Boolean(onLockedRemoval);
  const OptionComponent = useMemo(() => {
    if (!hasLockedRemoval) return CheckboxOption;
    return function LockAwareOption(props: RSOptionInnerProps) {
      if (!props.isDisabled) return <CheckboxOption {...props} />;
      const innerProps = { ...props.innerProps, onClick: () => onLockedRemovalRef.current?.([props.data as Option]) };
      return <CheckboxOption {...props} innerProps={innerProps} />;
    };
  }, [hasLockedRemoval]);

  const BaseOption = isMulti ? OptionComponent : RSOption;
  const ScrollToLabelOption = useMemo(() => (scrollToLabel ? buildScrollToLabelOption(BaseOption, scrollToLabel) : undefined), [BaseOption, scrollToLabel]);

  const selectComponents = {
    DropdownIndicator: BombiixDropdownIndicator,
    ClearIndicator: BombiixClearIndicator,
    ...(isMulti ? { MultiValueRemove: MultiValueRemoveComponent, Option: OptionComponent } : {}),
    ...(ScrollToLabelOption ? { Option: ScrollToLabelOption } : {}),
    ...(CustomMenuList ? { MenuList: CustomMenuList } : {}),
    ...(invalid ? { IndicatorsContainer: InvalidIndicatorsContainer } : {}),
  };
  const multiProps = isMulti ? { isMulti: true, closeMenuOnSelect: false, hideSelectedOptions: false } : {};

  const [searchText, setSearchText] = useState('');
  useEffect(() => {
    if (!menuOpen) setSearchText('');
  }, [menuOpen]);

  /**
   * Keeps the in-menu search text while the user picks. react-select resets its input after
   * every selection (`set-value`) and on blur / close, which wiped the search after each pick
   * and after Select All; only typed text is taken here, and the search resets once the menu
   * really closes (effect above).
   * @param {string} nextValue - The text react-select proposes for the search input.
   * @param {{ action: string }} actionMeta - Why react-select is changing the input.
   * @returns {void}
   */
  const handleSearchInputChange = useCallback((nextValue: string, actionMeta: { action: string }): void => {
    if (actionMeta.action === 'input-change') setSearchText(nextValue);
  }, []);
  const searchProps = isMulti && showSearch ? { inputValue: searchText, onInputChange: handleSearchInputChange } : {};
  /**
   * Closes a fixed menu when the page scrolls under it. A consumer's own `closeMenuOnScroll` decides
   * when that is (a grid cell keeps its menu with the cell while it is in view); without one, any
   * scroll outside the menu closes it. The controlled (menu-extras) menu is shut here, because
   * `handleMenuClose` keeps it open while focus is still in the control.
   * @param {Event} event - The scroll event.
   * @returns {boolean} Whether react-select should close the menu itself.
   */
  const closeOnPageScroll = (event: Event): boolean => {
    const consumerRule = (rest as { closeMenuOnScroll?: unknown }).closeMenuOnScroll;
    const shouldClose = typeof consumerRule === 'function' ? Boolean(Reflect.apply(consumerRule, undefined, [event])) : isScrollOutsideMenu(event);
    if (!shouldClose) {
      return false;
    }
    if (!hasMenuExtras) {
      return true;
    }
    setMenuOpen(false);
    rest.onMenuClose?.();
    return false;
  };

  /**
   * Resets keyboard highlighting on any mouse press and, while the controlled (menu-extras) menu is
   * open, marks a primary press on the chevron as a deliberate close: `handleMenuClose` would
   * otherwise keep the menu open because focus is still inside the control.
   * @param event - The mouse press inside the control.
   * @returns {void}
   */
  const handleContainerMouseDownCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    setIsKeyNavigating(false);
    const isChevronPress = event.button === 0 && event.target instanceof Element && event.target.closest('.bombiix-select__dropdown-indicator') !== null;
    chevronCloseRef.current = hasMenuExtras && menuOpen && isChevronPress;
  };

  return (
    <div className={rootClass} ref={containerRef} onMouseDownCapture={handleContainerMouseDownCapture}>
      {label ? (
        <label className={labelClass} htmlFor={inputId}>
          <span className="bombiix-select__label-text">{label}</span>
          {required || dppRequired ? (
            <span className={`bombiix-select__required${dppRequired ? ' bombiix-select__required--green' : ''}`} aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <div className="bombiix-select__control-wrap">
        <Select
          classNamePrefix="bombiix-select"
          styles={mergedStyles}
          inputId={inputId}
          components={selectComponents}
          placeholder={placeholder ?? ''}
          maxMenuHeight={maxMenuHeight}
          {...multiProps}
          {...rest}
          {...searchProps}
          onChange={handleChange}
          {...(isOptionDisabledProp ? { isOptionDisabled: lockedIfSelected } : {})}
          {...(isMulti && rest.isClearable === undefined ? { isClearable: false } : {})}
          {...controlledMenuProps}
          {...(rest.menuPosition === 'fixed' ? { closeMenuOnScroll: closeOnPageScroll } : {})}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
}

export default NewSelectMultiSelect;

const RSIndicatorsContainer = RSComponents.IndicatorsContainer as React.ComponentType<RSInnerProps>;

/**
 * Indicators row for the invalid state: the alert badge sits in the flex row ahead
 * of the clear and chevron icons, so it can never overlap them or paint over the
 * open menu the way an absolutely positioned overlay did.
 * @param {RSInnerProps} props - react-select indicators-container props.
 * @returns {React.ReactElement} The indicators row with the alert badge.
 */
function InvalidIndicatorsContainer({ children, ...props }: RSInnerProps) {
  return (
    <RSIndicatorsContainer {...props}>
      <span className="bombiix-select__alert-icon" aria-hidden="true">
        <i className="bombiix-icon-validation f-24" />
      </span>
      {children}
    </RSIndicatorsContainer>
  );
}
/**
 * A fixed menu is placed once, when it opens, and stays put while the page scrolls under it — so it
 * drifts away from its field. It closes on any scroll except the option list's own.
 * @param {Event} event - The scroll event.
 * @returns {boolean} Whether to close the menu.
 */
function isScrollOutsideMenu(event: Event): boolean {
  return !(event.target instanceof Element && event.target.closest('.bombiix-select__menu'));
}

const DEFAULT_MENU_MAX_HEIGHT = 200;

/**
 * Options the current search text keeps visible, resolved through react-select's own
 * `filterOption` / `getOptionLabel` / `getOptionValue` so Select All acts on exactly the
 * rows the user sees. An empty search or a `null` filter keeps every option.
 * @param {ReadonlyArray<unknown>} options - The full option list.
 * @param {string} inputValue - The current search text.
 * @param {RSMenuListInnerProps['selectProps']} selectProps - react-select's resolved props.
 * @returns {unknown[]} The options matching the search.
 */
function getSearchMatchedOptions(options: ReadonlyArray<unknown>, inputValue: string, selectProps: RSMenuListInnerProps['selectProps']): unknown[] {
  const filterOption = selectProps?.filterOption;
  if (!inputValue || !filterOption) return [...options];
  return options.filter((option) =>
    filterOption(
      {
        label: selectProps?.getOptionLabel?.(option) ?? '',
        value: selectProps?.getOptionValue?.(option) ?? '',
        data: option,
      },
      inputValue,
    ),
  );
}

/**
 * Wraps an option row so the row labelled `scrollToLabel` centres itself in the open list. Only
 * while nothing is selected and no search is typed: react-select already scrolls to a selected
 * value, and a filtered list should stay at its top. Runs as a passive effect so it lands after
 * react-select's own open-time scroll to its focused first row.
 * @param BaseOption - The option component being wrapped.
 * @param scrollToLabel - Label of the row to bring into view.
 * @returns The wrapping option component.
 */
function buildScrollToLabelOption(BaseOption: React.ComponentType<RSOptionInnerProps>, scrollToLabel: string) {
  return function ScrollToLabelOption(props: RSOptionInnerProps) {
    const optionId = props.innerProps?.id;
    const isScrollTarget = props.label === scrollToLabel && !props.hasValue && !props.selectProps?.inputValue;
    useEffect(() => {
      if (!isScrollTarget || typeof optionId !== 'string') return;
      const optionElement = document.getElementById(optionId);
      const menuList = optionElement?.parentElement;
      if (!optionElement || !menuList) return;
      menuList.scrollTop = optionElement.offsetTop - (menuList.clientHeight - optionElement.offsetHeight) / 2;
    }, [isScrollTarget, optionId]);
    return <BaseOption {...props} />;
  };
}
